"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const services = [
    {
        title: 'Brand Strategy',
        icon: (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="4" width="32" height="32" rx="4" stroke="#ceff1a" strokeWidth="1.5"/>
                <circle cx="20" cy="20" r="7" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M20 4v5M20 31v5M4 20h5M31 20h5" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="20" cy="20" r="2.5" fill="#ceff1a"/>
            </svg>
        ),
        desc: 'Strong brands are built on clarity. We define your positioning, audience insights, competitive advantage, messaging pillars, and brand narrative to create a foundation for long-term growth.',
    },
    {
        title: 'Consumer & Market Research',
        icon: (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="4" width="32" height="32" rx="4" stroke="#ceff1a" strokeWidth="1.5"/>
                <circle cx="18" cy="18" r="7" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M23 23l9 9" stroke="#ceff1a" strokeWidth="2" strokeLinecap="round"/>
                <path d="M15 18h6M18 15v6" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
        ),
        desc: 'Before we create, we understand. We analyse customer behaviour, motivations, objections, and decision-making patterns to uncover what truly influences buying decisions in your category.',
    },
    {
        title: 'Brand Identity Development',
        icon: (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="4" width="32" height="32" rx="4" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M12 28l4-4M20 10l3 3-9 9-3-3 9-9z" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M26 8l6 6-2 2-6-6 2-2z" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        desc: 'From visual identity systems and design direction to tone of voice and messaging frameworks, we build cohesive brands that create recognition and trust across every touchpoint.',
    },
    {
        title: 'Creative Strategy',
        icon: (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="4" width="32" height="32" rx="4" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M12 28c4-6 8-10 16-8" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="28" cy="14" r="5" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M14 18l4-6 4 4 4-6" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        desc: "Creative isn't decoration — it's communication. We develop creative strategies that ensure every campaign, asset, and touchpoint reinforces your positioning and moves your audience to action.",
    },
    {
        title: 'Content & Communication Frameworks',
        icon: (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="4" width="32" height="32" rx="4" stroke="#ceff1a" strokeWidth="1.5"/>
                <path d="M11 14h18M11 20h14M11 26h10" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M28 22l5 3-5 3" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        desc: 'We help businesses communicate consistently across websites, social media, advertising, and customer interactions — so your brand voice stays clear and compelling at every touchpoint.',
    },
];

const BORDER = '1px solid rgba(206,255,26,0.12)';

/* ── Individual card ── */
function ServiceCard({
    service,
    index,
    featured = false,
    className = '',
    style: extraStyle,
}: {
    service: typeof services[0];
    index: number;
    featured?: boolean;
    className?: string;
    style?: React.CSSProperties;
}) {
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: index * 0.08 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={className}
            style={{
                position: 'relative',
                padding: featured ? '44px 48px' : '36px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                minHeight: featured ? 300 : 260,
                background: hovered ? 'rgba(206,255,26,0.025)' : 'transparent',
                transition: 'background 0.3s ease',
                overflow: 'hidden',
                ...extraStyle,
            }}
        >
            {/* Icon box */}
            <div
                style={{
                    width: 76,
                    height: 76,
                    border: hovered
                        ? '1px solid rgba(206,255,26,0.4)'
                        : '1px solid rgba(206,255,26,0.18)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: hovered ? 'rgba(206,255,26,0.06)' : 'rgba(206,255,26,0.03)',
                    flexShrink: 0,
                    transition: 'border-color 0.3s ease, background 0.3s ease',
                }}
            >
                {service.icon}
            </div>

            {/* Title */}
            <h3
                style={{
                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    fontWeight: 700,
                    fontSize: featured ? 'clamp(20px, 2vw, 26px)' : 'clamp(15px, 1.3vw, 19px)',
                    color: '#fbfdfd',
                    letterSpacing: '0.02em',
                    lineHeight: 1.25,
                }}
            >
                {service.title}
            </h3>

            {/* Description */}
            <p
                style={{
                    fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                    fontSize: featured ? 15 : 13.5,
                    lineHeight: 1.78,
                    color: hovered ? 'rgba(251,253,253,0.72)' : 'rgba(251,253,253,0.50)',
                    letterSpacing: '0.025em',
                    transition: 'color 0.3s ease',
                }}
            >
                {service.desc}
            </p>

            {/* Bottom accent bar — slides in on hover */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: 2,
                    width: '100%',
                    background: '#ceff1a',
                    transform: hovered ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
            />
        </motion.div>
    );
}

/* ── Section ── */
export default function BrandServices() {
    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: '#080400' }}
        >
            <div className="max-w-[1400px] mx-auto flex flex-col gap-6 relative z-10">

                {/* Label */}
                <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: '#ceff1a',
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    }}
                >
                    What We Offer
                </motion.span>

                {/* Bento grid container */}
                <div
                    style={{
                        border: BORDER,
                        borderRadius: 24,
                        overflow: 'hidden',
                    }}
                >

                    {/* ── Row 1: Heading + Featured service ── */}
                    <div
                        className="grid grid-cols-1 md:grid-cols-5"
                        style={{ borderBottom: BORDER }}
                    >
                        {/* Heading block */}
                        <motion.div
                            initial={{ opacity: 0, x: -24 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.65 }}
                            className="md:col-span-2"
                            style={{
                                padding: '52px 48px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: 20,
                                borderRight: BORDER,
                            }}
                        >
                            <h2
                                style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700,
                                    fontSize: 'clamp(38px, 4.5vw, 68px)',
                                    lineHeight: 1.0,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                <span style={{ color: '#ceff1a', display: 'block' }}>Our</span>
                                <span
                                    style={{
                                        display: 'block',
                                        background: 'linear-gradient(180deg, #fbfdfd 0%, #9a9a9a 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    Branding
                                </span>
                                <span
                                    style={{
                                        display: 'block',
                                        background: 'linear-gradient(180deg, #fbfdfd 0%, #9a9a9a 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    Services
                                </span>
                            </h2>
                            <p
                                style={{
                                    fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                    fontSize: 14,
                                    color: 'rgba(251,253,253,0.40)',
                                    lineHeight: 1.7,
                                    letterSpacing: '0.03em',
                                    maxWidth: 280,
                                }}
                            >
                                Everything your brand needs to be understood, remembered, and chosen.
                            </p>
                        </motion.div>

                        {/* Featured: Brand Strategy */}
                        <ServiceCard service={services[0]} index={0} featured className="md:col-span-3" />
                    </div>

                    {/* ── Row 2: 4 remaining services ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                        {services.slice(1).map((s, i) => (
                            <ServiceCard
                                key={i}
                                service={s}
                                index={i + 1}
                                featured={false}
                                style={{
                                    borderLeft: i > 0 ? BORDER : undefined,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
